import React, {
  ComponentType
} from 'react'
import { connect } from 'react-redux'
import { Accordion } from 'semantic-ui-react'
import filter from 'lodash/filter'
// Local
import {
  CONDITION,
  ROLES
} from '../../../constant'
import {
  condition as conditionUtils,
  conditionGroupTitle,
  filterWithStatus,
  groupByAddressed,
  hasConditionsOrActivities,
  orderConditionsByHccConfidenceDescending
} from '../../common/conditionUtils'
import { groupByHccCode } from '../../common/hccUtils'
import {
  encounterConditionGroups,
  encounterConditionGroupsEnabled,
  hccGroupingEnabled
} from '../../common/currentUserUtils'
import ConditionHeader from '../common/conditionHeader'
import ClinicalAdminCondition from './clinicalAdminCondition'
import ClinicalAdminConditionHccGroup from './clinicalAdminConditionHccGroup'
import {
  EncounterConditionDisplay,
  EncounterDetail,
  User
} from '../../authenticated/types'
import { RootState } from '../../../store'
import { ObjectValues } from '../../../types/types'
import If from '../../common/if'
// Styling
import '../detail.css'

const SafeAccordion = Accordion as unknown as ComponentType<any>

type ConditionGroup = {
  hccCode: string | null
  hccDescription: string | null
  conditions: EncounterConditionDisplay[]
  addressed: boolean
}

type DisplayCondition = {
  title: string
  conditions: ConditionGroup[]
  hasInsightsToShow?: boolean
}

type ClinicalAdminEncounterConditionsOwnProps = {
  mode?: string | null
  encounterDetail: EncounterDetail
  conditionGroups?: string[]
  activeConditionId: string | null
  onConditionAction: (
    action: ObjectValues<typeof CONDITION.ACTION>,
    condition: EncounterConditionDisplay | string | null
  ) => void
}

type ClinicalAdminEncounterConditionsReduxProps = {
  currentUser: User
}

type ClinicalAdminEncounterConditionsProps =
  ClinicalAdminEncounterConditionsOwnProps &
  ClinicalAdminEncounterConditionsReduxProps

/**
 * Provides a component that renders the Clinical Admin encounter conditions.
 *
 * The Clinical Admin view of conditions is the simplest: it just displays
 * Activities, Known and Potential conditions. If a tenant property is enabled
 * Clinical Admin may be able to dismiss potential conditions
 *
 * @param mode View mode. Options: "smart", null. Default: null
 * @param encounterDetail Encounter detail entity
 * @param conditionGroups Optional condition groups to display; overrides tenant property
 * @param activeConditionId Active condition ID (default: null). This is the
 *        last condition or group that the user expanded
 * @param onConditionAction Callback for condition action.
 *        Arguments: action, condition
 */
function ClinicalAdminEncounterConditions(
  props: ClinicalAdminEncounterConditionsProps
): JSX.Element {
  const {
    mode: modeProp,
    encounterDetail,
    conditionGroups: conditionGroupsProp,
    activeConditionId: activeConditionIdProp,
    onConditionAction,
    currentUser
  } = props

  const groupConditions = (
    conditions: EncounterConditionDisplay[]
  ): ConditionGroup[] => {
    if (hccGroupingEnabled(currentUser)) {
      return groupByHccCode(conditions)
    }
    return groupByAddressed(conditions)
  }

  const filterConditions = (
    conditions: EncounterConditionDisplay[],
    conditionStatusCode: string
  ): EncounterConditionDisplay[] => {
    const ignoreCodes = [ CONDITION.CODE.REMOVED ]
    return orderConditionsByHccConfidenceDescending(
      filterWithStatus(
        ROLES.CLINICAL_ADMIN,
        conditions,
        conditionStatusCode,
        ignoreCodes
      )
    )
  }

  const sortConditionGroups = (groups: ConditionGroup[]): ConditionGroup[] => {
    const withHcc = groups.filter(g => g.hccCode !== null)
    const withHccAddressed = withHcc.filter(g => g.addressed === true)
    const withHccNotAddressed = withHcc.filter(g => g.addressed === false)
    const withoutHcc = groups.filter(g => g.hccCode === null)
    const withoutHccAddressed = withoutHcc.filter(g => g.addressed === true)
    const withoutHccNotAddressed = withoutHcc.filter(g => g.addressed === false)
    return withHccNotAddressed
      .concat(withoutHccNotAddressed)
      .concat(withHccAddressed)
      .concat(withoutHccAddressed)
  }

  const sortConditions = (
    conditions: EncounterConditionDisplay[]
  ): EncounterConditionDisplay[] => {
    const addressed = filter(conditions, condition => conditionUtils.isConditionAddressed(condition))

    const notAddressed = filter(conditions, condition => !conditionUtils.isConditionAddressed(condition))

    return notAddressed.concat(addressed)
  }

  const renderHccGroup = (
    group: ConditionGroup,
    index: number,
    groupTitle: string
  ): JSX.Element => (
    <ClinicalAdminConditionHccGroup
      key={ `${group.hccCode}-${index}` }
      mode={ modeProp }
      hccCode={ group.hccCode }
      hccDescription={ group.hccDescription }
      activeConditionId={ activeConditionIdProp }
      conditionGroupTitle={ groupTitle }
      conditions={ group.conditions }
      onConditionAction={ onConditionAction }
    />
  )

  const renderUngrouped = (
    group: ConditionGroup
  ): JSX.Element[] => group.conditions.map((condition, conditionIndex) => (
    <ClinicalAdminCondition
      key={ condition.id }
      mode={ modeProp }
      active={ activeConditionIdProp === condition.id }
      condition={ condition }
      displayIndex={ conditionIndex }
      onConditionAction={ onConditionAction }
    />
  ))

  const renderConditions = (
    displayConditions: DisplayCondition[]
  ): JSX.Element => {
    const detail = encounterDetail
    const key = detail.encounter
      ? detail.encounter.id
      : 'unknown'

    return (
      <div key={ key }>
        { displayConditions.map((display, displayIndex) => {
          const hasInsightsToShow = hasConditionsOrActivities(
            display.conditions,
            currentUser.type
          )

          return (
            <div key={ `condition-group-${display.title}` }>
              <ConditionHeader
                title={ display.title }
                displayIndex={ displayIndex }
                conditions={ display.conditions }
              />
              <If isTrue={ hasInsightsToShow }>
                <SafeAccordion styled fluid>
                  { display.conditions.map((group, groupIndex) => {
                    if (group.hccCode === null && group.conditions.length > 0) {
                      return renderUngrouped(group)
                    }
                    if (group.hccCode !== null && group.conditions.length > 0) {
                      return renderHccGroup(
                        group,
                        groupIndex,
                        display.title
                      )
                    }
                    return null
                  }) }
                </SafeAccordion>
              </If>
              <If isTrue={ !hasInsightsToShow }>
                <SafeAccordion styled fluid>
                  <Accordion.Content>
                    <span>{ `No ${display.title} to show at this time.` }</span>
                  </Accordion.Content>
                </SafeAccordion>
              </If>
            </div>
          )
        }) }
      </div>
    )
  }

  const detail = encounterDetail
  const conditions = detail.conditions || []
  const conditionGroups = encounterConditionGroups(
    currentUser,
    conditionGroupsProp && conditionGroupsProp.length > 0
      ? conditionGroupsProp[0]
      : undefined
  )
  const groupsEnabled = encounterConditionGroupsEnabled(currentUser)
  const displayConditions: DisplayCondition[] = []

  conditionGroups.forEach(g => {
    if (groupsEnabled[g] === true) {
      displayConditions.push({
        title: conditionGroupTitle(g as keyof typeof CONDITION.TYPE),
        conditions: sortConditionGroups(
          groupConditions(
            sortConditions(
              filterConditions(conditions, g)
            )
          )
        )
      })
    }
  })

  return renderConditions(displayConditions)
}

const mapStateToProps = (state: RootState): ClinicalAdminEncounterConditionsReduxProps => ({
  currentUser: state.user
})

export default connect(
  mapStateToProps,
  {}
)(ClinicalAdminEncounterConditions)
